import React, { useState, useEffect, useContext } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import './Login.css';
import { LanguageContext } from '../App';
import WorkspacePreferences from './WorkspacePreferences';

const Login = () => {
  const { language } = useContext(LanguageContext);
  const es = language === 'es';
  const t = (esText, enText) => (es ? esText : enText);
  const { login, loginWithGoogle, resetPassword, error: authError, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // Ocultar el navbar principal cuando estamos en login
  useEffect(() => {
    const navbar = document.querySelector('.navbar');
    const footer = document.querySelector('.footer');
    
    if (navbar) {
      navbar.style.display = 'none';
    }
    if (footer) {
      footer.style.display = 'none';
    }
    
    return () => {
      if (navbar) {
        navbar.style.display = 'flex';
      }
      if (footer) {
        footer.style.display = 'block';
      }
    };
  }, []);

  // Redirigir si ya está autenticado — panel único por rol (2026-09-21):
  // los dos roles caen en /dashboard, que muestra contenido distinto según
  // isAdmin (ver Dashboard.jsx). Ya no hay una URL separada para el ajustador.
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, navigate]);

  // Cargar email guardado
  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!email || !password) {
      setError(t('Por favor completa todos los campos', 'Please fill in all fields'));
      setLoading(false);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError(t('Ingresa un correo electrónico válido', 'Enter a valid email address'));
      setLoading(false);
      return;
    }

    const result = await login(email, password);

    if (result.success) {
      if (rememberMe) {
        localStorage.setItem('rememberedEmail', email);
      } else {
        localStorage.removeItem('rememberedEmail');
      }
      navigate('/dashboard');
    } else {
      setError(result.error || authError || t('Error al iniciar sesión. Verifica tus credenciales.', 'Could not sign in. Check your credentials.'));
    }

    setLoading(false);
  };

  // ← NUEVA FUNCIÓN PARA GOOGLE
  const handleGoogleLogin = async () => {
    setError('');
    setGoogleLoading(true);

    const result = await loginWithGoogle();

    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error || t('Error al iniciar sesión con Google', 'Could not sign in with Google'));
    }

    setGoogleLoading(false);
  };

  const handleForgotPassword = async () => {
    if (!email) {
      toast.error(t('Escribe tu correo electrónico primero.', 'Type your email first.'));
      return;
    }
    setResetLoading(true);
    const result = await resetPassword(email);
    if (result.success) {
      toast.success(t(`Enviamos un enlace de recuperación a ${email}.`, `We sent a recovery link to ${email}.`));
    } else {
      toast.error(result.error || t('No pudimos enviar el correo de recuperación.', 'We could not send the recovery email.'));
    }
    setResetLoading(false);
  };

  return (
    <div className="auth-login-container">
      <div className="auth-bg-decoration">
        <div className="auth-bg-circle auth-bg-circle-1"></div>
        <div className="auth-bg-circle auth-bg-circle-2"></div>
        <div className="auth-bg-circle auth-bg-circle-3"></div>
      </div>

      <div className="auth-login-card">
        <div className="workspace-portal-preferences"><WorkspacePreferences /></div>
        <div className="auth-login-header">
          <div className="auth-logo-wrapper">
            <div className="auth-logo-icon">
              <i className="fa-solid fa-shield-halved"></i>
            </div>
            <div className="auth-logo-text">CSR <span>Claims Assistant</span></div>
          </div>
          <h2>{t('Bienvenido de vuelta', 'Welcome back')}</h2>
          <p>{t('Accede a tu panel de control y gestiona tus reclamos', 'Sign in to your dashboard and manage your claims')}</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-login-form">
          <div className="auth-form-group">
            <div className="auth-input-wrapper">
              <i className="fa-solid fa-envelope auth-input-icon-left"></i>
              <input
                id="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder=" "
                required
                autoComplete="email"
                aria-invalid={(error || authError) ? 'true' : undefined}
                aria-describedby={(error || authError) ? 'login-error' : undefined}
                className={email ? 'auth-filled' : ''}
              />
              <label htmlFor="login-email">{t('Correo electrónico', 'Email')}</label>
            </div>
          </div>

          <div className="auth-form-group">
            <div className="auth-input-wrapper">
              <i className="fa-solid fa-lock auth-input-icon-left"></i>
              <input
                id="login-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder=" "
                required
                autoComplete="current-password"
                aria-invalid={(error || authError) ? 'true' : undefined}
                aria-describedby={(error || authError) ? 'login-error' : undefined}
                className={password ? 'auth-filled' : ''}
              />
              <label htmlFor="login-password">{t('Contraseña', 'Password')}</label>
              <button 
                type="button"
                className="auth-password-toggle"
                aria-label={showPassword ? (language === 'es' ? 'Ocultar contraseña' : 'Hide password') : (language === 'es' ? 'Mostrar contraseña' : 'Show password')}
                onClick={() => setShowPassword(!showPassword)}
              >
                <i className={`fa-solid ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
              </button>
            </div>
          </div>

          <div className="auth-form-options">
            <label className="auth-checkbox-label">
              <input 
                type="checkbox" 
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <span className="auth-checkmark"></span>
              {t('Recordarme', 'Remember me')}
            </label>
            <button
              type="button"
              className="auth-forgot-password"
              onClick={handleForgotPassword}
              disabled={resetLoading}
            >
              {resetLoading ? t('Enviando...', 'Sending...') : t('¿Olvidaste tu contraseña?', 'Forgot your password?')}
            </button>
          </div>

          {(error || authError) && (
            <div className="auth-error-message" id="login-error" role="alert">
              <i className="fa-solid fa-circle-exclamation" aria-hidden="true"></i>
              <span>{error || authError}</span>
            </div>
          )}

          <button type="submit" className="auth-btn-login" disabled={loading || googleLoading}>
            {loading ? (
              <>
                <i className="fa-solid fa-spinner fa-spin"></i>
                <span>{t('Verificando credenciales...', 'Checking credentials...')}</span>
              </>
            ) : (
              <>
                <span>{t('Ingresar', 'Sign in')}</span>
                <i className="fa-solid fa-arrow-right"></i>
              </>
            )}
          </button>

          {/* ← NUEVO DIVISOR Y BOTÓN DE GOOGLE */}
          <div className="auth-divider">
            <span>{t('o continúa con', 'or continue with')}</span>
          </div>

          <button 
            type="button" 
            className="auth-btn-google" 
            onClick={handleGoogleLogin}
            disabled={loading || googleLoading}
          >
            {googleLoading ? (
              <>
                <i className="fa-solid fa-spinner fa-spin"></i>
                <span>{t('Conectando con Google...', 'Connecting to Google...')}</span>
              </>
            ) : (
              <>
                <i className="fa-brands fa-google"></i>
                <span>{t('Continuar con Google', 'Continue with Google')}</span>
              </>
            )}
          </button>
        </form>

        <div className="auth-login-footer">
          <a href="/" className="auth-back-link">
            <i className="fa-solid fa-arrow-left"></i>
            {t('Volver al sitio web', 'Back to website')}
          </a>
        </div>
      </div>
    </div>
  );
};

export default Login;